import { el } from '../ui/dom.js';

/**
 * Upload / camera entry panel.
 *
 * The privacy promise is stated before the user picks a file, and the accepted
 * formats/sizes come from the document profile rather than from hardcoded text.
 */

export interface UploadPanelHandlers {
  onFile(file: File): void;
  onCamera(): void;
}

export interface UploadPanelOptions {
  documentName: string;
  formats: string[];
  maxBytes: number;
  minWidth: number;
  minHeight: number;
  maxWidth: number;
  maxHeight: number;
  cameraAvailable: boolean;
  busy: boolean;
  busyLabel?: string;
}

const formatBytes = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} МБ`
    : `${Math.round(bytes / 1024)} КБ`;

export const createUploadPanel = (
  handlers: UploadPanelHandlers,
  options: UploadPanelOptions,
): HTMLElement => {
  const input = el('input', {
    type: 'file',
    accept: 'image/jpeg,image/png,image/webp',
    class: 'pv-visually-hidden',
    id: 'pv-file-input',
  });
  input.addEventListener('change', () => {
    const file = input.files?.[0];
    if (file) handlers.onFile(file);
    input.value = '';
  });

  const drop = el('div', { class: 'pv-drop' }, [
    el('p', { class: 'pv-drop__title', text: 'Загрузите фотографию' }),
    el('p', {
      class: 'pv-drop__hint',
      text: `Перетащите файл сюда или выберите его на устройстве. Подходят: ${options.formats
        .map((format) => format.toUpperCase())
        .join(', ')} до ${formatBytes(options.maxBytes)}.`,
    }),
    el('div', { class: 'pv-cta__actions' }, [
      el('label', { class: 'pv-button pv-button--primary', for: 'pv-file-input', text: 'Выбрать файл' }),
      options.cameraAvailable
        ? (() => {
            const camera = el('button', { class: 'pv-button', type: 'button', text: '📷 Сделать фото сейчас' });
            camera.addEventListener('click', () => handlers.onCamera());
            return camera;
          })()
        : el('span', { class: 'pv-small pv-muted', text: 'Камера недоступна в этом браузере.' }),
    ]),
    input,
    el('p', {
      class: 'pv-drop__privacy',
      text: 'Фотография обрабатывается прямо в браузере и не отправляется на сервер. Мы не сохраняем снимки и не передаём их третьим лицам.',
    }),
    el('p', {
      class: 'pv-small pv-muted',
      text: `Для «${options.documentName}» подходит изображение от ${options.minWidth}×${options.minHeight} до ${options.maxWidth}×${options.maxHeight} px. Точные требования перечислены ниже.`,
    }),
  ]);

  let dragDepth = 0;
  drop.addEventListener('dragenter', (event) => {
    event.preventDefault();
    dragDepth += 1;
    drop.classList.add('pv-drop--active');
  });
  drop.addEventListener('dragover', (event) => {
    event.preventDefault();
  });
  drop.addEventListener('dragleave', () => {
    dragDepth = Math.max(0, dragDepth - 1);
    if (dragDepth === 0) drop.classList.remove('pv-drop--active');
  });
  drop.addEventListener('drop', (event) => {
    event.preventDefault();
    dragDepth = 0;
    drop.classList.remove('pv-drop--active');
    const file = event.dataTransfer?.files?.[0];
    if (file) handlers.onFile(file);
  });

  if (options.busy) {
    const progress = el('div', { class: 'pv-progress', role: 'status' }, [
      el('span', { class: 'pv-spinner' }),
      el('span', { text: options.busyLabel ?? 'Обрабатываем фотографию…' }),
    ]);
    drop.append(progress);
  }

  return el('section', { class: 'pv-panel' }, [drop]);
};

export const createErrorPanel = (message: string): HTMLElement =>
  el('section', { class: 'pv-panel' }, [
    el('div', { class: 'pv-error', role: 'alert', text: message }),
  ]);

/**
 * Shown when the browser cannot run local computer vision.
 *
 * No stack traces, no technical identifiers: a short explanation, what is
 * missing in plain words, and what the user can do instead.
 */
export const createUnsupportedPanel = (report: {
  reason?: string;
  missing: string[];
  degraded: string[];
}): HTMLElement =>
  el('section', { class: 'pv-panel' }, [
    el('div', { class: 'pv-unsupported', role: 'alert' }, [
      el('p', {
        class: 'pv-unsupported__title',
        text: report.reason ?? 'Браузер не поддерживает локальную проверку изображения.',
      }),
      el('p', {
        class: 'pv-small',
        text:
          'Проверка фотографии выполняется на вашем устройстве: изображение не отправляется на сервер. ' +
          'Для этого нужны современные возможности браузера (WebAssembly и работа с изображениями).',
      }),
      el('p', {
        class: 'pv-small pv-muted',
        text: 'Откройте валидатор в Chrome, Safari, Edge или Firefox последней версии — на компьютере или телефоне.',
      }),
      el('p', {
        class: 'pv-small pv-muted',
        text: 'Требования к фотографии для выбранного документа можно прочитать на этой странице ниже.',
      }),
    ]),
  ]);

export const createProgressPanel = (label: string): HTMLElement =>
  el('section', { class: 'pv-panel' }, [
    el('div', { class: 'pv-progress', role: 'status' }, [
      el('span', { class: 'pv-spinner' }),
      el('span', { text: label }),
    ]),
  ]);
